import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  Suspense,
  lazy,
  useRef,
} from "react";
import {
  CCard,
  CCardHeader,
  CCardBody,
  CTab,
  CTabs,
  CTabContent,
  CTabPanel,
  CTabList,
  CSpinner,
  CRow,
  CCol,
  CButton,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilWarning } from "@coreui/icons";
import { apiClient } from "services/Api";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/modules/gantt";
import "highcharts/modules/exporting";
import "highcharts/modules/offline-exporting";

import LoadingScreen from "components/globals/LoadingScreen";
import { getGanttOptions } from "./utils";
import { triggerAlert } from "components/globals/AlertNotification";
import FilterPanel from "./FilterPanel";
import PaymentExportModal from "./modals/PaymentExport";
import CodeMatrixModal from "./modals/CodeMatrixModal";
import ProjectPendingChangesModal from "./modals/ProjectPendingChangesModal";

const TeamSalaries = lazy(() =>
  import("./pages/shared/teamSalaries/TeamSalariesPage")
);

const ActivitiesANI = lazy(() => import("./pages/ani/Activities"));
const TeamANI = lazy(() => import("./pages/ani/Team"));
const SheetANI = lazy(() => import("./pages/ani/Sheet"));
const ImputationANI = lazy(() => import("./pages/ani/Imputation"));

const ActivitiesIAPMEI = lazy(() => import("./pages/iapmei/Activities"));
const TeamIAPMEI = lazy(() => import("./pages/iapmei/Team"));
const SheetIAPMEI = lazy(() => import("./pages/iapmei/Sheet"));
const ImputationIAPMEI = lazy(() => import("./pages/iapmei/Imputation"));

const ActivitiesOTHER = lazy(() => import("./pages/other/Activities"));
const TeamOTHER = lazy(() => import("./pages/other/Team"));
const SheetOTHER = lazy(() => import("./pages/other/Sheet"));
const ImputationOTHER = lazy(() => import("./pages/other/Imputation"));

const Project = ({
  clientId,
  projectId,
  setToast,
  setHandleSave,
  setHandleDelete,
  setFilterButton,
  setExtractFiles,
  setTimesheetExporter,
}) => {
  const sheetTabButtonRef = useRef(null);
  const salariesTabButtonRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("activities");
  const [project, setProject] = useState({});
  const [range, setRange] = useState({});
  const [activities, setActivities] = useState([]);
  const activitiesDebounceRef = useRef(null);

  const setActivitiesDebounced = useCallback((value) => {
    if (activitiesDebounceRef.current) {
      clearTimeout(activitiesDebounceRef.current);
    }
    activitiesDebounceRef.current = setTimeout(() => {
      setActivities(value);
    }, 400);
  }, []);

  useEffect(() => {
    return () => {
      if (activitiesDebounceRef.current) {
        clearTimeout(activitiesDebounceRef.current);
      }
    };
  }, []);

  const [showPaymentExport, setShowPaymentExport] = useState(false);
  const [showCodeMatrix, setShowCodeMatrix] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [sheetReviewRequest, setSheetReviewRequest] = useState(null);

  const responsibleEntity = useMemo(() => {
    const val = project?.management?.responsible_entity ?? "";
    return String(val).toUpperCase().trim();
  }, [project]);

  const investmentMethodology = useMemo(() => {
    const val = project?.management?.investment_methodology ?? "";
    return String(val).toUpperCase().trim();
  }, [project]);

  const pendingChanges = useMemo(() => project.pending, [project]);

  const TeamComponent = useMemo(
    () =>
      responsibleEntity === "IAPMEI"
        ? TeamIAPMEI
        : responsibleEntity === "ANI"
        ? TeamANI
        : TeamOTHER,
    [responsibleEntity]
  );
  const ActivitiesComponent = useMemo(
    () =>
      responsibleEntity === "IAPMEI"
        ? ActivitiesIAPMEI
        : responsibleEntity === "ANI"
        ? ActivitiesANI
        : ActivitiesOTHER,
    [responsibleEntity]
  );
  const SheetComponent = useMemo(
    () =>
      responsibleEntity === "IAPMEI"
        ? SheetIAPMEI
        : responsibleEntity === "ANI"
        ? SheetANI
        : SheetOTHER,
    [responsibleEntity]
  );
  const ImputationComponent = useMemo(
    () =>
      responsibleEntity === "IAPMEI"
        ? ImputationIAPMEI
        : responsibleEntity === "ANI"
        ? ImputationANI
        : ImputationOTHER,
    [responsibleEntity]
  );

  const fetchProject = useCallback(() => {
    return apiClient
      .get(`/projects/management/${clientId}/${projectId}/project/`)
      .then((response) => {
        setProject(response.data);
      })
      .catch((error) => console.error("Erro ao carregar projeto:", error));
  }, [clientId, projectId]);

  useEffect(() => {
    fetchProject().then(() => {
      setRange({});
      setLoading(false);
    });
  }, [fetchProject]);

  useEffect(() => {
    setFilterButton(
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <FilterPanel project={project.management} onApply={setRange} />
        {pendingChanges && (
          <CButton
            color="warning"
            className="d-flex align-items-center"
            onClick={() => setShowPendingModal(true)}
          >
            <CIcon icon={cilWarning} className="me-2" size="lg" />
            Alt. Pendentes
          </CButton>
        )}
      </div>
    );
  }, [project, pendingChanges, setFilterButton, setRange, setShowPendingModal]);

  const focusSheetTab = useCallback(() => {
    const btn = sheetTabButtonRef.current;
    if (btn && typeof btn.click === "function") {
      btn.click();
      return true;
    }
    return false;
  }, []);

  const handleReviewRequest = useCallback(
    (personId, target) => {
      setShowPendingModal(false);
      if (target === "salaries") {
        // Clicar no separador (e não só mudar o estado) deixa o painel
        // ficar visível antes de a grelha montar — o Handsontable rebenta
        // se montar num painel ainda escondido. Sem o separador (projeto
        // não é de custos reais / sem acesso), revê-se nas Sheets.
        const btn = salariesTabButtonRef.current;
        if (btn && typeof btn.click === "function") {
          btn.click();
          return;
        }
      }
      setSheetReviewRequest({ personId, ts: Date.now() });
      if (!focusSheetTab()) {
        setActiveTab("sheet");
      }
    },
    [focusSheetTab]
  );

  const triggerDownloadFromUrl = useCallback((downloadUrl) => {
    window.location.assign(downloadUrl);
  }, []);

  const waitForTimesheetExport = useCallback(
    async (taskId) => {
      const maxAttempts = 60; // ~5 min com 5s
      const delayMs = 5000;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const resp = await apiClient.get(
          `/projects/management/export/task/${taskId}/`,
          {
            responseType: "json",
            validateStatus: () => true,
          }
        );

        if (resp.status === 202) {
          await new Promise((r) => setTimeout(r, delayMs));
          continue;
        }

        if (resp.status === 200 && resp.data?.download_url) {
          triggerDownloadFromUrl(resp.data.download_url);
          return;
        }

        throw new Error(resp.data?.error || "Exportação falhou");
      }

      throw new Error("Tempo de espera esgotado");
    },
    [triggerDownloadFromUrl]
  );

  // `period` = { start, end } em YYYY-MM (filtro temporal da exportação).
  const extractTimesheet = useCallback(
    async (period = {}) => {
      let startResp;
      try {
        startResp = await apiClient.get(
          `/projects/management/${clientId}/${projectId}/project/`,
          {
            params: {
              file: "excel",
              ...(period.start ? { start: period.start } : {}),
              ...(period.end ? { end: period.end } : {}),
            },
            responseType: "json",
          }
        );
      } catch (error) {
        throw new Error(
          error?.response?.data?.error || "Não foi possível iniciar a exportação"
        );
      }

      if (startResp.status === 200 && startResp.data?.download_url) {
        triggerDownloadFromUrl(startResp.data.download_url);
        return;
      }

      if (startResp.status === 202 && startResp.data?.task_id) {
        await waitForTimesheetExport(startResp.data.task_id);
        return;
      }

      throw new Error("Não foi possível iniciar a exportação");
    },
    [clientId, projectId, waitForTimesheetExport, triggerDownloadFromUrl]
  );

  useEffect(() => {
    if (!setTimesheetExporter) return;
    setTimesheetExporter({
      run: extractTimesheet,
      management: project?.management,
    });
    return () => setTimesheetExporter(null);
  }, [extractTimesheet, project, setTimesheetExporter]);

  // A Timesheet é registada à parte (setTimesheetExporter): no ClientPage
  // abre o cartão de filtro temporal dentro do dropdown "Exportar Dados".
  useEffect(() => {
    if (responsibleEntity === "IAPMEI") {
      setExtractFiles({
        "Pedido de Pagamento": (setLoading) => {
          setShowPaymentExport(true);
        },
      });
    } else if (responsibleEntity === "ANI") {
      setExtractFiles({
        SGO: () => {
          setShowCodeMatrix(true);
        },
      });
    } else {
      setExtractFiles(null);
    }
  }, [responsibleEntity, setExtractFiles, setShowPaymentExport]);

  const chartOptions = useMemo(
    () =>
      getGanttOptions(
        project.management?.startDate,
        project.management?.endDate,
        project.management?.executed,
        activities
      ),
    [activities, project]
  );

  const deleteProject = useCallback(() => {
    apiClient
      .delete(`/projects/management/${clientId}/${projectId}/project/`)
      .then((response) => {
        triggerAlert("Projeto eliminado com sucesso!", "success");
        const url = new URL(window.location.href);

        // Remover o parâmetro "id"
        url.searchParams.delete("projeto");
        window.history.replaceState({}, "", url);
        window.location.reload();
      })
      .catch(() => {
        setToast({
          title: "Projeto",
          type: "error",
          message: "Ocorreu um erro a eliminar o projeto.",
        });
      });
  }, [clientId, projectId]);

  useEffect(() => {
    setHandleDelete(() => deleteProject);
  }, [deleteProject]);

  if (loading) {
    return <LoadingScreen message="A carregar projeto..." />;
  }

  return (
    <>
      {/* Cronograma */}
      <CCard className="mb-2">
        <CCardHeader>
          <div className="d-flex justify-content-center align-items-center">
            <h4 className="m-0">{project.name}</h4>
          </div>
        </CCardHeader>
        <CCardBody>
          <HighchartsReact
            highcharts={Highcharts}
            style={{ maxHeight: 600, overflowY: "auto" }}
            options={chartOptions}
            constructorType={"ganttChart"}
          />
        </CCardBody>
      </CCard>

      {/* Tabs */}
      <CCard>
        <CTabs activeItemKey={activeTab} onChange={setActiveTab}>
          <CTabList variant="tabs">
            <CTab itemKey="activities">Atividades</CTab>
            <CTab itemKey="team">Equipa</CTab>
            {project.salary_access_allowed && project.project_is_salary_based && (
              <CTab itemKey="salaries" ref={salariesTabButtonRef}>
                Salários
              </CTab>
            )}
            <CTab itemKey="sheet" ref={sheetTabButtonRef}>
              Sheets
            </CTab>
            {responsibleEntity !== "Other" && <CTab itemKey="kpi">Resumo</CTab>}
          </CTabList>
          <CTabContent>
            <CTabPanel className="p-3" itemKey="activities">
              <Suspense fallback={<PainelLoading />}>
                {activeTab === "activities" && (
                  <ActivitiesComponent
                    clientId={clientId}
                    projectId={projectId}
                    projectState={project.state}
                    setToast={setToast}
                    onChange={setActivitiesDebounced}
                    setHandleSave={setHandleSave}
                  />
                )}
              </Suspense>
            </CTabPanel>
            <CTabPanel className="p-3" itemKey="team">
              <Suspense fallback={<PainelLoading />}>
                {activeTab === "team" && (
                  <TeamComponent
                    clientId={clientId}
                    projectId={projectId}
                    setToast={setToast}
                    setHandleSave={setHandleSave}
                  />
                )}
              </Suspense>
            </CTabPanel>
            {project.salary_access_allowed && project.project_is_salary_based && (
              <CTabPanel className="p-3" itemKey="salaries">
                <Suspense fallback={<PainelLoading />}>
                  {activeTab === "salaries" && (
                    <TeamSalaries
                      clientId={clientId}
                      projectId={projectId}
                      setToast={setToast}
                      setHandleSave={setHandleSave}
                    />
                  )}
                </Suspense>
              </CTabPanel>
            )}
            <CTabPanel className="p-3" itemKey="sheet">
              <Suspense fallback={<PainelLoading />}>
                {activeTab === "sheet" && (
                  <SheetComponent
                    clientId={clientId}
                    projectId={projectId}
                    setToast={setToast}
                    filterRange={range}
                    projectState={project.state}
                    investmentMethodology={investmentMethodology}
                    setHandleSave={setHandleSave}
                    reviewRequest={sheetReviewRequest}
                    completeReview={() => setSheetReviewRequest(null)}
                    onProjectRefresh={fetchProject}
                  />
                )}
              </Suspense>
            </CTabPanel>
            <CTabPanel className="p-3" itemKey="kpi">
              {activeTab === "kpi" && (
                <Suspense fallback={<PainelLoading />}>
                  <ImputationComponent
                    clientId={clientId}
                    projectId={projectId}
                    setToast={setToast}
                    filterRange={range}
                    projectState={project.state}
                    setHandleSave={setHandleSave}
                  />
                </Suspense>
              )}
            </CTabPanel>
          </CTabContent>
        </CTabs>
      </CCard>

      {showPaymentExport && (
        <PaymentExportModal
          clientId={clientId}
          projectId={projectId}
          onClose={() => setShowPaymentExport(false)}
          setToast={setToast}
        />
      )}

      {showCodeMatrix && (
        <CodeMatrixModal
          clientId={clientId}
          projectId={projectId}
          onClose={() => setShowCodeMatrix(false)}
          setToast={setToast}
        />
      )}

      {showPendingModal && (
        <ProjectPendingChangesModal
          clientId={clientId}
          projectId={projectId}
          onClose={() => setShowPendingModal(false)}
          setToast={setToast}
          onApplied={fetchProject}
          onRequestReview={handleReviewRequest}
          projectState={project.state}
        />
      )}
    </>
  );
};

export default Project;

const PainelLoading = () => (
  <div className="p-5 text-center">
    <CSpinner color="primary" style={{ width: "4rem", height: "4rem" }} />
    <CRow className="mt-3">
      <CCol className="text-center">
        <span className="text-muted fs-5 fw-semibold">A carregar página</span>
      </CCol>
    </CRow>
  </div>
);
